import React, { FC, useRef } from 'react';
import Svg from 'svg-inline-react';
import classnames from 'classnames';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Appearance, Button } from '@src/components/Button';
import ChevronRightSvg from '@src/svgs/fr-chevron-right.svg';
import { OpeningStateEnum, useSessionQuery } from '@src/graphql/generated';
import FrankLogoWhiteSvg from '@src/svgs/frank-logo-new.svg';
import ChevronLeftSvg from '@src/svgs/fr-chevron-left.svg';
import { ActionMenuPopover } from '@src/components/Popover';
import { Position } from '@src/components/Popover/ActionMenuPopover/enums/position.enum';
import FrCheckSvg from '@src/svgs/fr-check.svg';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { Save } from '../Save';
import { ConnectHideActions } from './ConnectHideActions';
import { FooterProps } from './props';
import './styles.scss';

const NOT_PUBLISHED_MESSAGE = 'This opening is not Published. You can invite candidates only for Published openings.';
const IN_REVIEW_MESSAGE = 'This opening is in Review. You can start inviting candidates once it’s approved.';

export const Footer: FC<FooterProps> = ({
  onConfirm,
  activeOpeningId,
  isInvited,
  onDiscard,
  goBack,
  goNext,
  areActionsEnabled,
  openingState,
  user,
  isFeaturedApplicantModal,
  isHideActionVisible,
  onVirtualRecruiterClick,
}) => {
  const { data: sessionData } = useSessionQuery();

  const isSiteAdmin = sessionData?.currentRecruiter?.isSiteAdmin;

  const messageRef = useRef<HTMLDivElement>(null);

  const hasTwoLines = messageRef.current ? messageRef.current.clientHeight > 40 : false;

  const inactiveMessage = openingState === OpeningStateEnum.ADMIN_REVIEW ? IN_REVIEW_MESSAGE : NOT_PUBLISHED_MESSAGE;

  const getShareUrl = () => {
    return window.location.href;
  };

  const copyAction = (
    <CopyToClipboard
      onCopy={() => {
        trackEvent({
          context: MetricContext.SOURCE,
          interaction: MetricAction.COPY_TALENT_URL,
          payload: {
            'User Id': user?.id,
          },
        });
      }}
      text={getShareUrl()}
    >
      <span>Copy profile url</span>
    </CopyToClipboard>
  );

  const actions = [{ label: copyAction }];

  return (
    <div
      className={classnames('pipeline-talent-card-footer', hasTwoLines && 'pipeline-talent-card-footer--less-padding')}
    >
      {isSiteAdmin && (
        <div className="pipeline-talent-card-footer__agent-button" onClick={() => onVirtualRecruiterClick()}>
          <div className="pipeline-talent-card-footer__agent-logo-wrapper">
            <Svg className="pipeline-talent-card-footer__agent-logo" src={FrankLogoWhiteSvg} />
          </div>
          Virtual Agency
        </div>
      )}

      <div className="pipeline-talent-card-footer__actions">
        <Button
          appearance={Appearance.PRIMARY}
          className="pipeline-talent-card-footer__button-arrow pipeline-talent-card-footer__button-arrow--left"
          onClick={() => goBack?.()}
        >
          <Svg className="pipeline-talent-card-footer__arrow-icon" src={ChevronLeftSvg} />
        </Button>

        {isInvited ? (
          <div className="pipeline-talent-card-footer__invite-sent fade-in">
            <Svg className="pipeline-talent-card-footer__invite-sent-icon" src={FrCheckSvg} />
            Invite sent
          </div>
        ) : (
          <ConnectHideActions
            activeOpeningId={activeOpeningId}
            areActionsEnabled={!!areActionsEnabled}
            inactiveMessage={inactiveMessage}
            isHideActionVisible={isHideActionVisible}
            onConfirmClick={onConfirm}
            onDiscard={!isFeaturedApplicantModal ? onDiscard : undefined}
            openingState={openingState}
          />
        )}

        <Button
          appearance={Appearance.PRIMARY}
          className="pipeline-talent-card-footer__button-arrow pipeline-talent-card-footer__button-arrow--left"
          onClick={() => goNext?.()}
        >
          <Svg className="pipeline-talent-card-footer__arrow-icon" src={ChevronRightSvg} />
        </Button>
      </div>

      <div className="pipeline-talent-card-footer__additional-actions">
        {user && <Save user={user} />}

        {!isFeaturedApplicantModal && (
          <ActionMenuPopover
            actions={actions}
            onAction={actionItem => {
              const clickedAction = actions[actionItem.index];

              if (clickedAction.action) {
                clickedAction.action();
              }
            }}
            position={Position.TOP}
          />
        )}
      </div>
    </div>
  );
};
